import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Drawer,
  Menu,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore,
  PlayArrow,
  ContentCopy,
  CheckCircle,
  Error,
  Info,
  Http,
  GetApp,
  PostAdd,
  Delete,
  Edit,
  Search,
  CameraAlt,
  VpnKey,
  CloudSync,
  Storefront,
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
  SmartToy,
  Close,
  FileDownload,
  HelpOutline,
  LocalOffer,
} from '@mui/icons-material';
import { useApiKey } from '../contexts/ApiKeyContext';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { tokens } from '../theme/tokens';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { apiService } from '../services/api';
import { getApiUrl } from '../utils/apiConfig';
import { AIHelpButton } from '../components/AIHelpButton';
import { useAIAgent } from '../contexts/AIAgentContext';
import {
  ApiTestEndpoint,
  buildRequestDetails,
  buildSubmissionFields,
  snippetAuthHeaders,
  SubmissionField,
} from '../utils/apiTestRequest';
import {
  buildPostmanCollection,
  buildPostmanCollectionFromSpec,
  downloadJsonFile,
  OpenApiSpecLike,
} from '../utils/postmanCollection';
import {
  API_ENDPOINTS,
  REBATE_PROGRAMS_CATEGORY,
  filterFlagGatedEndpoints,
  flagGatedSpecPathsToOmit,
} from '../utils/apiTestEndpoints';
import openApiSpec from '../generated/openapi.json';
import { EmptyState, PageContainer, PageHeader } from '../components/ui';
import { useToast } from '../contexts/ToastContext';

type ApiEndpoint = ApiTestEndpoint;

interface TestResult {
  endpoint: string;
  method: string;
  status: 'success' | 'error' | 'loading';
  response?: any;
  error?: string;
  timestamp: Date;
  requestUrl?: string;
  submissionFields?: SubmissionField[];
}

type SnippetLanguageOption = 'curl' | 'javascript' | 'node' | 'python';

const SNIPPET_LANGUAGE_OPTIONS: Array<{ value: SnippetLanguageOption; label: string }> = [
  { value: 'curl', label: 'cURL' },
  { value: 'javascript', label: 'JavaScript (Fetch)' },
  { value: 'node', label: 'Node.js (Axios)' },
  { value: 'python', label: 'Python (Requests)' },
];

// sx color tokens resolved against the theme palette
const METHOD_COLORS: Record<string, string> = {
  GET: 'success.main',
  POST: 'info.main',
  PUT: 'warning.main',
  DELETE: 'error.main',
};

/** Single auth badge — backend accepts API key or session for most protected routes. */
const PLAN_BADGES: Record<NonNullable<ApiEndpoint['plan']>, { label: string; tooltip: string }> = {
  public: { label: 'No plan needed', tooltip: 'Public demo data. Works without an API key on every plan.' },
  all: { label: 'All paid plans', tooltip: 'Included with Starter, Pro, Intelligence and Complete API keys.' },
  pro: {
    label: 'Pro and above',
    tooltip: 'Starter API keys receive 403 here. Upgrade to Pro to read the full Products API.',
  },
  session: { label: 'Dashboard login', tooltip: 'Managed from a logged-in Motzu session, not an API key.' },
};

function endpointPlanBadge(endpoint: Pick<ApiEndpoint, 'plan'>): { label: string; tooltip: string } | null {
  return endpoint.plan ? PLAN_BADGES[endpoint.plan] : null;
}

function endpointAuthBadge(endpoint: Pick<ApiEndpoint, 'requiresAuth' | 'requiresApiKey'>): {
  label: string;
  color: 'default' | 'warning' | 'success';
  tooltip: string;
} {
  if (!endpoint.requiresAuth && !endpoint.requiresApiKey) {
    return {
      label: 'Public',
      color: 'success',
      tooltip: 'No authentication required.',
    };
  }
  if (endpoint.requiresApiKey && endpoint.requiresAuth) {
    return {
      label: 'API key or login',
      color: 'warning',
      tooltip:
        'Authenticate with an API key (X-API-Key) or a logged-in Motzu session (Bearer). Either is enough — you do not need both.',
    };
  }
  if (endpoint.requiresApiKey) {
    return {
      label: 'API key',
      color: 'warning',
      tooltip: 'Requires an API key via the X-API-Key header.',
    };
  }
  return {
    label: 'Login required',
    color: 'warning',
    tooltip: 'Requires a logged-in Motzu session (Bearer ID token). This route does not accept an API key.',
  };
}

const SNIPPET_BASE_URL = process.env.REACT_APP_SNIPPET_BASE_URL || 'https://api.motzulabs.com';

export const ApiTestPage: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { isEnabled: cachedOrgFlagEnabled, isOnUnlessOff: isUiSwitchOn, isError: orgFlagsFailed } = useOrgFeatureFlags();
  // Fail closed: a failed refetch keeps React Query's cached flags, so a flag
  // turned off since must not stay on here while the flags cannot be read.
  const isOrgFlagEnabled = (key: Parameters<typeof cachedOrgFlagEnabled>[0]): boolean =>
    !orgFlagsFailed && cachedOrgFlagEnabled(key);
  const { apiKey, apiKeys } = useApiKey();
  const { userProfile, currentUser } = useAuth();
  const { isImpersonating, impersonatedOrg, getEffectiveRole } = useImpersonation();
  // Gate admin-only copy and endpoint groups by the EFFECTIVE role, so an admin
  // impersonating a brand sees exactly what the brand sees.
  const effectiveRole = getEffectiveRole(userProfile);
  const isAdminView = effectiveRole === 'admin';
  // Store Overrides are store-scoped; they do not apply to a brand (vendor) shell.
  // Flag-gated groups (Rebate Programs: `rebate_rules_api`) show only while the
  // organization's flag is on.
  const visibleEndpoints = filterFlagGatedEndpoints(
    effectiveRole === 'vendor'
      ? API_ENDPOINTS.filter((endpoint) => endpoint.category !== 'Store Overrides')
      : API_ENDPOINTS,
    isOrgFlagEnabled,
  );

  const [pickedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null);
  // A picked request that a flag change has since hidden is no longer
  // selected, so its form, Test action and single-request export go with it.
  const selectedEndpoint =
    pickedEndpoint && visibleEndpoints.some((endpoint) => endpoint.id === pickedEndpoint.id) ? pickedEndpoint : null;
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | false>(false);
  const [isTesting, setIsTesting] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());
  const [snippetLanguage, setSnippetLanguage] = useState<SnippetLanguageOption>('javascript');
  const [snippet, setSnippet] = useState('');
  const [isSnippetLoading, setIsSnippetLoading] = useState(false);
  const [snippetError, setSnippetError] = useState<string | null>(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [snippetDrawerOpen, setSnippetDrawerOpen] = useState(false);
  const [aiLanguage, setAiLanguage] = useState<SnippetLanguageOption>('javascript');
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiSnippet, setAiSnippet] = useState('');
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [postmanMenuAnchor, setPostmanMenuAnchor] = useState<null | HTMLElement>(null);
  const { showToast } = useToast();

  const resolvedOrgId =
    impersonatedOrg?.id || userProfile?.primaryOrganizationId || userProfile?.organizationId || null;
  const requestContext = {
    organizationId: resolvedOrgId,
    organizationName: impersonatedOrg?.name || null,
    impersonating: Boolean(isImpersonating && impersonatedOrg?.id),
    apiKey,
    hasSession: Boolean(currentUser),
  };

  // The key's permissions come from the key list this page's provider already
  // fetched. If the active key is not in that list (still loading, or a key
  // saved from another account) its scopes are unknown, so the banner stays
  // neutral rather than guessing.
  const activeKeyRecord = apiKey ? apiKeys.find((candidate) => candidate.key === apiKey) : undefined;
  const activeKeyHasNoScopes =
    activeKeyRecord !== undefined && (activeKeyRecord.permissions ?? []).length === 0;
  const maskedApiKey = apiKey ? `${apiKey.substring(0, 8)}...${apiKey.substring(apiKey.length - 4)}` : '';

  const submissionFields = selectedEndpoint
    ? buildSubmissionFields(selectedEndpoint, parameters, requestBody, requestContext)
    : [];
  const selectedRequestDetails = selectedEndpoint
    ? buildRequestDetails(selectedEndpoint, parameters, requestBody)
    : null;

  useEffect(() => {
    if (!snippetDrawerOpen) {
      setAiDialogOpen(false);
    }
  }, [snippetDrawerOpen]);

  useEffect(() => {
    if (snippetDrawerOpen && selectedEndpoint) {
      generateSnippet(snippetLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndpoint, snippetDrawerOpen]);

  const buildSnippetHeaders = (endpoint: ApiEndpoint, parsedBody: unknown, includeOptionalAuth: boolean = true): Record<string, string> => {
    const headers: Record<string, string> = { ...snippetAuthHeaders(endpoint) };

    if (Object.keys(headers).length === 0 && includeOptionalAuth) {
      headers['X-API-Key'] = '<YOUR_API_KEY> (optional)';
    }

    if (parsedBody && endpoint.method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  };

  const handleEndpointSelect = (endpoint: ApiEndpoint) => {
    setSelectedEndpoint(endpoint);
    setParameters({});
    setRequestBody('');
    setSnippet('');
    setSnippetError(null);
    setAiSnippet('');
    setAiError(null);
    
    // Set default values for parameters
    if (endpoint.parameters) {
      const defaultParams: Record<string, string> = {};
      endpoint.parameters.forEach(param => {
        if (param.example) {
          defaultParams[param.name] = param.example;
        }
      });
      setParameters(defaultParams);
    }

    // Set default request body
    if (endpoint.body) {
      const defaultBody: Record<string, any> = {};
      endpoint.body.forEach(field => {
        if (field.example !== undefined) {
          defaultBody[field.name] = field.example;
        }
      });
      setRequestBody(JSON.stringify(defaultBody, null, 2));
    }
  };

  const handleTestEndpoint = async () => {
    if (!selectedEndpoint || isTesting) return;

    setIsTesting(true);
    const testResult: TestResult = {
      endpoint: selectedEndpoint.path,
      method: selectedEndpoint.method,
      status: 'loading',
      timestamp: new Date(),
      requestUrl: buildRequestDetails(selectedEndpoint, parameters, requestBody).relativeUrl,
      submissionFields: buildSubmissionFields(selectedEndpoint, parameters, requestBody, requestContext),
    };

    setTestResults((prev) => [testResult, ...prev]);

    try {
      const requestDetails = buildRequestDetails(selectedEndpoint, parameters, requestBody);
      const relativeUrl = requestDetails.relativeUrl;
      const targetUrl = `${apiService.baseURL}${relativeUrl}`;
      const normalizedBody = (() => {
        if (!requestDetails.parsedBody) {
          return undefined;
        }

        if (typeof requestDetails.parsedBody === 'string') {
          try {
            return JSON.parse(requestDetails.parsedBody);
          } catch {
            return requestDetails.parsedBody;
          }
        }

        return requestDetails.parsedBody;
      })();

      let response: any;

      if (selectedEndpoint.method === 'GET') {
        if (selectedEndpoint.id === 'health') {
          const healthUrl = `${apiService.baseURL}/health`;
          response = await fetch(healthUrl, { headers: await apiService.getHeaders() }).then((res) => res.json());
        } else if (selectedEndpoint.id === 'product-showcase') {
          const data = await apiService.getProductShowcase();
          response = { success: true, data, count: data.length };
        } else if (selectedEndpoint.id === 'product-showcase-by-id') {
          const data = await apiService.getProductShowcaseById(parameters.id);
          response = { success: true, data };
        } else if (selectedEndpoint.id === 'camera-stats' || selectedEndpoint.id === 'product-stats') {
          response = await apiService.getCameraStats();
        } else if (selectedEndpoint.id === 'get-cameras') {
          const limit = parameters.limit ? parseInt(parameters.limit) : 10;
          const offset = parameters.offset ? parseInt(parameters.offset) : 0;
          response = await apiService.getCameras(limit, offset);
        } else if (selectedEndpoint.id === 'get-camera-by-id') {
          response = await apiService.getCameraById(parameters.id);
        } else if (selectedEndpoint.id === 'search-cameras') {
          const limit = parameters.limit ? parseInt(parameters.limit) : 10;
          const offset = parameters.offset ? parseInt(parameters.offset) : 0;
          response = await apiService.searchCameras(parameters.q, limit, offset);
        } else if (selectedEndpoint.id === 'get-cameras-by-category') {
          const limit = parameters.limit ? parseInt(parameters.limit) : 10;
          const offset = parameters.offset ? parseInt(parameters.offset) : 0;
          response = await apiService.getCamerasByCategory(parameters.category, limit, offset);
        } else if (selectedEndpoint.id === 'get-cameras-by-brand') {
          const limit = parameters.limit ? parseInt(parameters.limit) : 10;
          const offset = parameters.offset ? parseInt(parameters.offset) : 0;
          response = await apiService.getCamerasByBrand(parameters.brand, limit, offset);
        } else if (selectedEndpoint.id === 'get-api-keys') {
          response = await apiService.getApiKeys();
        } else if (selectedEndpoint.id === 'get-integrations') {
          response = await apiService.getIntegrations();
        } else if (selectedEndpoint.id === 'get-store-product-overrides') {
          if (parameters.productId) {
            const override = await apiService.getStoreProductOverride(parameters.productId);
            response = { success: true, override };
          } else {
            response = await fetch(targetUrl, {
              headers: await apiService.getHeaders(),
            }).then((res) => res.json());
          }
        } else {
          response = await fetch(targetUrl, {
            headers: await apiService.getHeaders(),
          }).then((res) => res.json());
        }
      } else if (selectedEndpoint.method === 'POST') {
        if (selectedEndpoint.id === 'create-api-key') {
          const body =
            normalizedBody && typeof normalizedBody === 'object'
              ? (normalizedBody as Record<string, unknown>)
              : {};
          const name = typeof body.name === 'string' ? body.name : 'New Key';
          const permissions = Array.isArray(body.permissions)
            ? (body.permissions as string[])
            : undefined;
          response = await apiService.createApiKey(name, permissions);
        } else if (selectedEndpoint.id === 'create-integration') {
          const payload = normalizedBody && typeof normalizedBody === 'object' ? normalizedBody : {};
          response = await apiService.createIntegration(payload);
        } else {
          const payload = normalizedBody && typeof normalizedBody === 'object' ? normalizedBody : {};
          response = await fetch(targetUrl, {
            method: 'POST',
            headers: await apiService.getHeaders(),
            body: JSON.stringify(payload),
          }).then((res) => res.json());
        }
      } else if (selectedEndpoint.method === 'PUT') {
        if (selectedEndpoint.id === 'upsert-store-product-override') {
          const payload =
            normalizedBody && typeof normalizedBody === 'object'
              ? (normalizedBody as Record<string, unknown>)
              : {};
          // Body field examples may be JSON strings for arrays/objects — parse overrideTags if needed
          if (typeof payload.overrideTags === 'string') {
            try {
              payload.overrideTags = JSON.parse(payload.overrideTags as string);
            } catch {
              payload.overrideTags = String(payload.overrideTags)
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean);
            }
          }
          const override = await apiService.upsertStoreProductOverride(parameters.productId, payload as any);
          response = { success: true, override };
        } else {
          const payload = normalizedBody && typeof normalizedBody === 'object' ? normalizedBody : {};
          response = await fetch(targetUrl, {
            method: 'PUT',
            headers: {
              ...(await apiService.getHeaders()),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          }).then((res) => res.json());
        }
      } else if (selectedEndpoint.method === 'DELETE') {
        if (selectedEndpoint.id === 'delete-api-key') {
          await apiService.deleteApiKey(parameters.id);
          response = { success: true };
        } else if (selectedEndpoint.id === 'delete-store-product-override') {
          await apiService.deleteStoreProductOverride(parameters.productId);
          response = { success: true, message: 'Product override deleted successfully' };
        } else {
          response = await fetch(targetUrl, {
            method: 'DELETE',
            headers: await apiService.getHeaders(),
          }).then((res) => res.json());
        }
      }

      setTestResults((prev) =>
        prev.map((result) =>
          result.timestamp === testResult.timestamp
            ? { ...result, status: 'success' as const, response }
            : result
        )
      );
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      setTestResults((prev) =>
        prev.map((result) =>
          result.timestamp === testResult.timestamp
            ? {
                ...result,
                status: 'error' as const,
                error: errorMessage,
              }
            : result
        )
      );
    } finally {
      setIsTesting(false);
    }
  };

  const handleExportPostman = (mode: 'selected' | 'all') => {
    if (mode === 'selected' && !selectedEndpoint) return;
    // One request keeps the tester's current values; the whole collection is generated from the OpenAPI spec.
    const collection =
      mode === 'all'
        ? buildPostmanCollectionFromSpec({
            spec: openApiSpec as unknown as OpenApiSpecLike,
            baseUrl: SNIPPET_BASE_URL,
            apiKey: apiKey || undefined,
            // Same flag gate as the tester: no rebate paths while `rebate_rules_api` is off.
            omitPaths: flagGatedSpecPathsToOmit(API_ENDPOINTS, isOrgFlagEnabled),
          })
        : buildPostmanCollection({
            endpoints: visibleEndpoints,
            baseUrl: SNIPPET_BASE_URL,
            selectedEndpointId: selectedEndpoint?.id,
            paramValues: parameters,
            requestBody,
            mode,
            apiKey: apiKey || undefined,
            organizationId: resolvedOrgId || undefined,
          });

    const filename =
      mode === 'selected' && selectedEndpoint
        ? `motzu-${selectedEndpoint.id}.postman_collection.json`
        : 'motzu-api.postman_collection.json';
    downloadJsonFile(filename, collection);
    setPostmanMenuAnchor(null);
    showToast(
      mode === 'selected'
        ? 'Downloaded this request. Import the file in Postman via File → Import.'
        : 'Downloaded the Motzu API collection. Import the file in Postman via File → Import, then set the apiKey variable.',
      'info',
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const clearResults = () => {
    setTestResults([]);
    setExpandedResults(new Set());
  };

  const toggleResultExpansion = (index: number) => {
    setExpandedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'GET': return <GetApp />;
      case 'POST': return <PostAdd />;
      case 'PUT': return <Edit />;
      case 'DELETE': return <Delete />;
      default: return <Http />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Product Data': return <CameraAlt />;
      case 'API Keys': return <VpnKey />;
      case 'Integrations': return <CloudSync />;
      case 'Store Overrides': return <Storefront />;
      case REBATE_PROGRAMS_CATEGORY: return <LocalOffer />;
      default: return <Http />;
    }
  };

  const groupedEndpoints = visibleEndpoints.reduce((acc, endpoint) => {
    if (!acc[endpoint.category]) {
      acc[endpoint.category] = [];
    }
    acc[endpoint.category].push(endpoint);
    return acc;
  }, {} as Record<string, ApiEndpoint[]>);

  const { openWithContext } = useAIAgent();

  const handleAIHelp = () => {
    openWithContext?.('Help me test and configure API endpoints');
  };

  const generateSnippet = async (language: SnippetLanguageOption = snippetLanguage) => {
    if (!selectedEndpoint) return;

    const requestDetails = buildRequestDetails(selectedEndpoint, parameters, requestBody);
    const headers = buildSnippetHeaders(selectedEndpoint, requestDetails.parsedBody);
    const relativeUrl = requestDetails.relativeUrl.startsWith('/')
      ? requestDetails.relativeUrl
      : `/${requestDetails.relativeUrl}`;
    const snippetUrl = `${SNIPPET_BASE_URL}${relativeUrl}`.replace(/:([a-zA-Z0-9_]+)/g, (_, name) => `<${name}>`);

    setIsSnippetLoading(true);
    setSnippetError(null);

    try {
      const response = await apiService.generateCodeSnippet({
        language,
        method: selectedEndpoint.method,
        url: snippetUrl,
        headers,
        body: requestDetails.parsedBody,
        description: selectedEndpoint.description,
      });
      setSnippet(response.snippet);
    } catch (error: any) {
      setSnippet('');
      setSnippetError(error?.message || 'Failed to generate code snippet');
    } finally {
      setIsSnippetLoading(false);
    }
  };

  const openSnippetDrawer = () => {
    if (!selectedEndpoint) return;
    setSnippetDrawerOpen(true);
  };

  const openAiDialog = () => {
    if (!selectedEndpoint) return;
    setAiLanguage(snippetLanguage);
    setAiInstructions('');
    setAiSnippet('');
    setAiError(null);
    openSnippetDrawer();
    setAiDialogOpen(true);
  };

  const handleCloseAiDialog = () => {
    if (!isAIGenerating) {
      setAiDialogOpen(false);
    }
  };

  const handleGenerateAiSnippet = async () => {
    if (!selectedEndpoint) return;

    const requestDetails = buildRequestDetails(selectedEndpoint, parameters, requestBody);
    const headers = buildSnippetHeaders(selectedEndpoint, requestDetails.parsedBody);
    const relativeUrl = requestDetails.relativeUrl.startsWith('/')
      ? requestDetails.relativeUrl
      : `/${requestDetails.relativeUrl}`;
    const snippetUrl = `${SNIPPET_BASE_URL}${relativeUrl}`.replace(/:([a-zA-Z0-9_]+)/g, (_, name) => `<${name}>`);

    setIsAIGenerating(true);
    setAiError(null);

    try {
      const response = await apiService.generateAICodeSnippet({
        language: aiLanguage,
        method: selectedEndpoint.method,
        url: snippetUrl,
        headers,
        body: requestDetails.parsedBody,
        description: selectedEndpoint.description,
        instructions: aiInstructions,
      });
      setAiSnippet(response.snippet);
    } catch (error: any) {
      setAiSnippet('');
      setAiError(error?.message || 'Failed to generate AI code snippet');
    } finally {
      setIsAIGenerating(false);
    }
  };

  if (!isUiSwitchOn('ui_api_access')) {
    return (
      <PageContainer>
        <Alert severity="info">
          API access is currently disabled for this account.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer>
        <PageHeader
          title="API Testing"
          description="Test and explore the Product Data API endpoints. Select an endpoint to view details and make calls."
          actions={
            <AIHelpButton
              onClick={handleAIHelp}
              variant="icon"
              tooltip="Ask Mozi, the AI assistant, for help testing and configuring API endpoints"
              ariaLabel="Ask Mozi AI assistant for help with API testing"
            />
          }
        />

        {/* API Base URL — browser tests use same-origin /api; snippets use public API host */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 2, 
            p: 1.5, 
            bgcolor: 'grey.100', 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1} flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              This page calls:
            </Typography>
            <Box 
              component="code" 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                bgcolor: 'grey.900', 
                color: 'info.light', 
                borderRadius: 1, 
                fontFamily: tokens.font.mono,
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.800' },
              }}
              onClick={() => {
                const absolute =
                  getApiUrl().startsWith('http')
                    ? getApiUrl()
                    : `${window.location.origin}${getApiUrl()}`;
                navigator.clipboard.writeText(absolute);
              }}
              title="Click to copy (same-origin hosting rewrite used by this UI)"
            >
              {getApiUrl().startsWith('http')
                ? getApiUrl()
                : `${typeof window !== 'undefined' ? window.location.origin : ''}${getApiUrl()}`}
            </Box>
            {/* The hosting note used to sit under both rows as grey internal notes; it now lives behind this tooltip. */}
            <Tooltip
              title={`Browser tests use the same-origin /api hosting rewrite (not a missing route). Code snippets use the public API host. Store override calls need a store org.${
                isAdminView ? ' Platform admins should impersonate one first.' : ''
              }`}
            >
              <IconButton size="small" aria-label="About these base URLs">
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1} flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              External / snippet base:
            </Typography>
            <Box 
              component="code" 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                bgcolor: 'grey.900', 
                color: 'info.light', 
                borderRadius: 1, 
                fontFamily: tokens.font.mono,
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.800' },
              }}
              onClick={() => {
                navigator.clipboard.writeText(SNIPPET_BASE_URL);
              }}
              title="Click to copy (public API host for curl/SDK examples)"
            >
              {SNIPPET_BASE_URL}
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Browser tests use the same-origin <code>/api</code> hosting rewrite (not a missing route). Code snippets
            use the public API host.
            {isAdminView && ' Store override calls need a store org — platform admins should impersonate one first.'}
          </Typography>

        </Paper>

        {/* API Key Status */}
        {apiKey ? (
          activeKeyHasNoScopes ? (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button component={RouterLink} to="/app/api-keys" color="inherit" size="small">
                  Manage API keys
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>API key has no permissions:</strong> {maskedApiKey}. This key has no endpoint
                permissions — calls will be rejected.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>API Key Active:</strong> {maskedApiKey}
              </Typography>
            </Alert>
          )
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>No API Key:</strong> Some endpoints require authentication. Generate an API key in the API Keys section.
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 220px)', minHeight: 0 }}>
          {/* Endpoints List */}
          <Box sx={{ width: '40%', minWidth: 380, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Sizes to its content (capped at the column height) so five collapsed groups no longer sit above a large empty block. */}
            <Paper elevation={1} sx={{ 
              p: 2, 
              position: 'sticky', 
              top: 80, 
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexShrink: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Available Endpoints
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={(event) => setPostmanMenuAnchor(event.currentTarget)}
                  sx={{ textTransform: 'none', fontWeight: 500, flexShrink: 0 }}
                >
                  Postman
                </Button>
              </Box>
              <Box sx={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
              {Object.entries(groupedEndpoints).map(([category, endpoints]) => (
              <Accordion
                key={category}
                expanded={expandedEndpoint === category}
                onChange={(_, isExpanded) => setExpandedEndpoint(isExpanded ? category : false)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getCategoryIcon(category)}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {category}
                    </Typography>
                    <Chip label={endpoints.length} size="small" color="primary" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {endpoints.map((endpoint) => (
                      <Card
                        key={endpoint.id}
                        sx={{
                          cursor: 'pointer',
                          border: selectedEndpoint?.id === endpoint.id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                          '&:hover': { borderColor: 'primary.main' },
                          borderRadius: 2
                        }}
                        onClick={() => handleEndpointSelect(endpoint)}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip
                              label={endpoint.method}
                              size="small"
                              sx={{
                                backgroundColor: METHOD_COLORS[endpoint.method],
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            />
                            <Typography variant="body2" sx={{ fontFamily: tokens.font.mono, flex: 1 }}>
                              {endpoint.path}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {endpoint.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            {(() => {
                              const badge = endpointAuthBadge(endpoint);
                              return (
                                <Tooltip title={badge.tooltip}>
                                  <Chip
                                    label={badge.label}
                                    size="small"
                                    color={badge.color === 'default' ? undefined : badge.color}
                                    variant={badge.color === 'success' ? 'outlined' : 'filled'}
                                  />
                                </Tooltip>
                              );
                            })()}
                            {(() => {
                              const planBadge = endpointPlanBadge(endpoint);
                              return planBadge ? (
                                <Tooltip title={planBadge.tooltip}>
                                  <Chip label={planBadge.label} size="small" variant="outlined" />
                                </Tooltip>
                              ) : null;
                            })()}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
              ))}
              </Box>
            </Paper>
          </Box>

          {/* Test Interface */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0 }}>
            {selectedEndpoint ? (
              <>
                {/* Endpoint Details */}
                <Card sx={{ mb: 3, borderRadius: 2, flexShrink: 0 }}>
                  <CardContent sx={{ pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={selectedEndpoint.method}
                        sx={{
                          backgroundColor: METHOD_COLORS[selectedEndpoint.method],
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                      <Typography variant="h6" sx={{ fontFamily: tokens.font.mono, flex: 1 }}>
                        {selectedEndpoint.path}
                      </Typography>
                      {(() => {
                        const badge = endpointAuthBadge(selectedEndpoint);
                        return (
                          <Tooltip title={badge.tooltip}>
                            <Chip
                              label={badge.label}
                              size="small"
                              color={badge.color === 'default' ? undefined : badge.color}
                              variant={badge.color === 'success' ? 'outlined' : 'filled'}
                            />
                          </Tooltip>
                        );
                      })()}
                      {(() => {
                        const planBadge = endpointPlanBadge(selectedEndpoint);
                        return planBadge ? (
                          <Tooltip title={planBadge.tooltip}>
                            <Chip label={planBadge.label} size="small" variant="outlined" />
                          </Tooltip>
                        ) : null;
                      })()}
                      <Tooltip title="Copy endpoint path">
                        <IconButton onClick={() => copyToClipboard(selectedEndpoint.path)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedEndpoint.description}
                    </Typography>
                    
                    {/* Parameters */}
                    {((selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0) ||
                      selectedEndpoint.orgScoped) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Parameters:
                        </Typography>
                        {selectedEndpoint.parameters?.map((param) => (
                          <Box key={param.name} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                            <TextField
                              label={param.name}
                              value={parameters[param.name] || ''}
                              onChange={(e) => setParameters(prev => ({ ...prev, [param.name]: e.target.value }))}
                              size="small"
                              sx={{ minWidth: 150 }}
                              placeholder={param.example}
                              required={param.required}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                              {param.type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              {param.description}
                            </Typography>
                          </Box>
                        ))}
                        {selectedEndpoint.orgScoped && (
                          <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'flex-start' }}>
                            <TextField
                              label="organizationId"
                              value={resolvedOrgId || ''}
                              size="small"
                              sx={{ minWidth: 150 }}
                              disabled
                              error={!resolvedOrgId}
                              helperText={
                                resolvedOrgId
                                  ? impersonatedOrg?.name
                                    ? `${impersonatedOrg.name} (session)`
                                    : 'From your session or API key'
                                  : 'Not set'
                              }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, pt: 1 }}>
                              context
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pt: 1 }}>
                              Not a query param. Resolved from impersonation, API key, or your store org.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Request Body */}
                    {selectedEndpoint.body && selectedEndpoint.body.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Request Body:
                        </Typography>
                        <TextField
                          multiline
                          rows={6}
                          fullWidth
                          value={requestBody}
                          onChange={(e) => setRequestBody(e.target.value)}
                          placeholder="Enter JSON request body..."
                          sx={{ fontFamily: tokens.font.mono }}
                        />
                      </Box>
                    )}

                    {/* Values actually submitted (includes session-resolved org, headers) */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Values being sent
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: tokens.font.mono, mb: 1.5, wordBreak: 'break-all' }}
                        >
                          {selectedEndpoint.method} {selectedRequestDetails?.relativeUrl}
                        </Typography>
                        {submissionFields.length === 0 ? (
                          <EmptyState size="compact" title="No parameters, body, or auth context for this request." />
                        ) : (
                          <Stack spacing={1}>
                            {submissionFields.map((field) => (
                              <Box
                                key={`${field.location}-${field.name}`}
                                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
                              >
                                <Chip
                                  label={field.location}
                                  size="small"
                                  variant="outlined"
                                  color={field.location === 'context' ? 'warning' : 'default'}
                                  sx={{ textTransform: 'capitalize', minWidth: 76 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: tokens.font.mono, fontWeight: 600, minWidth: 140 }}
                                >
                                  {field.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: tokens.font.mono,
                                    flex: 1,
                                    minWidth: 120,
                                    color: field.missing ? 'error.main' : 'text.primary',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {field.missing
                                    ? 'not set'
                                    : field.location === 'body' && field.value.length > 280
                                      ? `${field.value.slice(0, 280)}…`
                                      : field.value}
                                </Typography>
                                {(field.location === 'context' || field.location === 'header' || field.missing) &&
                                  field.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
                                    {field.description}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                        {selectedEndpoint.orgScoped && !resolvedOrgId && (
                          <Alert severity="warning" sx={{ mt: 1.5 }}>
                            {isAdminView
                              ? 'Store override calls need a store organization. Impersonate a store org, then retry.'
                              : 'Store override calls need a store organization.'}
                          </Alert>
                        )}
                      </Paper>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={2}>
                      <Button
                        variant="contained"
                        startIcon={isTesting ? <CircularProgress size={20} /> : <PlayArrow />}
                        onClick={handleTestEndpoint}
                        disabled={
                          // API-key-only routes need a key; session-or-key routes can use login alone.
                          (selectedEndpoint.requiresApiKey &&
                            !selectedEndpoint.requiresAuth &&
                            !apiKey) ||
                          isTesting
                        }
                        sx={{ flex: 1, minHeight: '42px', textTransform: 'none', fontWeight: 500 }}
                      >
                        {isTesting ? 'Testing...' : 'Test Endpoint'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={openSnippetDrawer}
                        disabled={!selectedEndpoint}
                        sx={{ minHeight: '42px', textTransform: 'none', fontWeight: 500 }}
                      >
                        View Code Examples
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={(event) => setPostmanMenuAnchor(event.currentTarget)}
                        sx={{ minHeight: '42px', textTransform: 'none', fontWeight: 500 }}
                      >
                        Export to Postman
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Test Results */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Test Results {testResults.length > 0 && `(${testResults.length})`}
                    </Typography>
                    {testResults.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={clearResults}
                        disabled={isTesting}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                  
                  {testResults.length === 0 ? (
                    <Paper sx={{ borderRadius: 2 }}>
                      <EmptyState
                        size="compact"
                        title="No test results yet"
                        description='Select an endpoint and click "Test Endpoint" to get started.'
                      />
                    </Paper>
                  ) : (
                    <Box sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 2, 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'grey.100',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#888',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: '#555',
                        },
                      },
                    }}>
                      {testResults.map((result, index) => {
                        const isExpanded = expandedResults.has(index);
                        const responseString = result.response ? JSON.stringify(result.response, null, 2) : '';
                        const responseLength = responseString.length;
                        const shouldTruncate = responseLength > 5000; // Truncate if longer than 5000 chars
                        const truncatedResponse = shouldTruncate && !isExpanded 
                          ? responseString.substring(0, 5000) + '\n... (truncated, click to expand)'
                          : responseString;

                        return (
                          <Card key={index} sx={{ borderRadius: 2, flexShrink: 0 }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                  label={result.method}
                                  size="small"
                                  sx={{
                                    backgroundColor: METHOD_COLORS[result.method],
                                    color: 'white',
                                    fontWeight: 'bold',
                                  }}
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: tokens.font.mono, 
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={result.requestUrl || result.endpoint}
                                >
                                  {result.requestUrl || result.endpoint}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                  {result.timestamp.toLocaleTimeString()}
                                </Typography>
                                {result.status === 'loading' && <CircularProgress size={20} />}
                                {result.status === 'success' && <CheckCircle color="success" fontSize="small" />}
                                {result.status === 'error' && <Error color="error" fontSize="small" />}
                                {result.status !== 'loading' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PlayArrow />}
                                    onClick={handleTestEndpoint}
                                    disabled={isTesting}
                                    sx={{ flexShrink: 0 }}
                                  >
                                    Test Again
                                  </Button>
                                )}
                              </Box>

                              {result.submissionFields && result.submissionFields.length > 0 && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Submitted
                                  </Typography>
                                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {result.submissionFields.map((field) => (
                                      <Typography
                                        key={`${field.location}-${field.name}`}
                                        variant="caption"
                                        sx={{ fontFamily: tokens.font.mono, display: 'block', wordBreak: 'break-word' }}
                                        color={field.missing ? 'error.main' : 'text.secondary'}
                                      >
                                        {field.location} {field.name}: {field.missing ? 'not set' : field.value}
                                      </Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              )}

                              {result.status === 'success' && result.response && (
                                <Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                      Response:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      {shouldTruncate && (
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => toggleResultExpansion(index)}
                                          startIcon={isExpanded ? <ExpandLess /> : <ExpandMoreIcon />}
                                          sx={{ minWidth: 'auto', px: 1 }}
                                        >
                                          {isExpanded ? 'Collapse' : 'Expand'}
                                        </Button>
                                      )}
                                      <Tooltip title="Copy response to clipboard">
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(responseString)}
                                          sx={{ minWidth: 'auto' }}
                                        >
                                          <ContentCopy fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Paper sx={{ 
                                    p: 2, 
                                    backgroundColor: 'grey.900', 
                                    color: 'grey.100', 
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                  }}>
                                    <Box sx={{ 
                                      overflow: 'auto',
                                      maxHeight: isExpanded ? 'none' : '500px',
                                      minHeight: '100px',
                                    }}>
                                      <pre style={{ 
                                        margin: 0, 
                                        fontSize: '0.875rem',
                                        lineHeight: 1.5,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: tokens.font.mono,
                                      }}>
                                        {truncatedResponse}
                                      </pre>
                                    </Box>
                                    {shouldTruncate && !isExpanded && (
                                      <Box sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '40px',
                                        background: 'linear-gradient(to top, rgba(11, 16, 32, 0.95), transparent)',
                                        pointerEvents: 'none',
                                      }} />
                                    )}
                                  </Paper>
                                  {responseLength > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      Response size: {responseLength.toLocaleString()} characters
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {result.status === 'error' && result.error && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Error:</strong> {result.error}
                                  </Typography>
                                </Alert>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Http sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Select an Endpoint
                </Typography>
                <Typography>
                  Choose an API endpoint from the list to view its details and test it.
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </PageContainer>

      <Drawer
        anchor="right"
        open={snippetDrawerOpen}
        onClose={() => setSnippetDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560, md: 640 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Code Examples
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedEndpoint ? selectedEndpoint.path : 'Select an endpoint'}
            </Typography>
          </Box>
          <IconButton onClick={() => setSnippetDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflow: 'auto' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="drawer-snippet-language-label">Language</InputLabel>
              <Select
                labelId="drawer-snippet-language-label"
                label="Language"
                value={snippetLanguage}
                onChange={(event) => {
                  const value = event.target.value as SnippetLanguageOption;
                  setSnippetLanguage(value);
                  generateSnippet(value);
                }}
              >
                {SNIPPET_LANGUAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => generateSnippet(snippetLanguage)}
              disabled={!selectedEndpoint || isSnippetLoading}
              startIcon={isSnippetLoading ? <CircularProgress size={18} /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {isSnippetLoading ? 'Generating...' : 'Generate Snippet'}
            </Button>
            <Tooltip title={snippet ? 'Copy snippet' : 'Generate snippet first'}>
              <span>
                <IconButton
                  disabled={!snippet}
                  onClick={() => snippet && copyToClipboard(snippet)}
                  sx={{ border: '1px solid', borderColor: snippet ? 'divider' : 'transparent' }}
                >
                  <ContentCopy />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="text"
              startIcon={<SmartToy />}
              onClick={openAiDialog}
              disabled={!selectedEndpoint}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Generate with AI
            </Button>
          </Stack>

          {snippetError && <Alert severity="error">{snippetError}</Alert>}
          {!snippet && !snippetError && !isSnippetLoading && (
            <Alert severity="info">
              Generate a sample request in your preferred language. Update parameters/body above and refresh to keep the
              code in sync.
            </Alert>
          )}

          {snippet && (
            <Paper
              sx={{
                p: 2,
                backgroundColor: 'grey.900',
                color: 'grey.100',
                borderRadius: 2,
                maxHeight: 360,
                overflow: 'auto',
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre',
                  fontFamily: tokens.font.mono,
                }}
              >
                {snippet}
              </pre>
            </Paper>
          )}
        </Box>
      </Drawer>

      <Dialog open={aiDialogOpen} onClose={handleCloseAiDialog} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Generate Code with AI</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Ask Mozi to craft a language-specific example for this endpoint. Add any extra
              instructions to tailor the snippet to your integration.
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="ai-language-label">Language</InputLabel>
              <Select
                labelId="ai-language-label"
                label="Language"
                value={aiLanguage}
                onChange={(event) => setAiLanguage(event.target.value as SnippetLanguageOption)}
              >
                {SNIPPET_LANGUAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Additional instructions"
              multiline
              minRows={3}
              value={aiInstructions}
              onChange={(event) => setAiInstructions(event.target.value)}
              placeholder="e.g. Include pagination handling and wrap the request in an async function"
              fullWidth
            />
            {aiError && <Alert severity="error">{aiError}</Alert>}
            {aiSnippet && (
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: 'grey.900',
                  color: 'grey.100',
                  borderRadius: 2,
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip title="Copy AI snippet">
                    <span>
                      <IconButton onClick={() => copyToClipboard(aiSnippet)}>
                        <ContentCopy />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre',
                    fontFamily: tokens.font.mono,
                  }}
                >
                  {aiSnippet}
                </pre>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAiDialog} disabled={isAIGenerating}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={isAIGenerating ? <CircularProgress size={18} /> : <SmartToy />}
            onClick={handleGenerateAiSnippet}
            disabled={isAIGenerating}
          >
            {isAIGenerating ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={postmanMenuAnchor}
        open={Boolean(postmanMenuAnchor)}
        onClose={() => setPostmanMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExportPostman('selected')} disabled={!selectedEndpoint}>
          This request
        </MenuItem>
        <MenuItem onClick={() => handleExportPostman('all')}>All endpoints</MenuItem>
      </Menu>
    </>
  );
};
